import { useCallback, useEffect, useState } from "react";
import { Check, type LucideIcon } from "lucide-react";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";

interface CopyMenuItemProps {
  // Produces the text to copy, evaluated at click time.
  getText: () => string;
  idleLabel: string;
  icon: LucideIcon;
  // Toast shown on a successful copy.
  successMessage: string;
}

// A row inside the turn actions menu that copies text to the clipboard and
// flips to a checkmark confirmation for a beat. Shared by the copy-output and
// copy-link actions so their state, toast, and layout stay identical.
export const CopyMenuItem = ({
  getText,
  idleLabel,
  icon: IdleIcon,
  successMessage,
}: CopyMenuItemProps) => {
  const { showSuccess, showError } = useToast();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getText());
      setCopied(true);
      showSuccess(successMessage);
    } catch {
      showError("Couldn't copy");
    }
  }, [getText, successMessage, showSuccess, showError]);

  const Icon = copied ? Check : IdleIcon;
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-sm",
        "text-foreground transition-colors hover:bg-muted"
      )}
    >
      <Icon className="size-4 text-muted-foreground" />
      {copied ? "Copied" : idleLabel}
    </button>
  );
};
